import { type ReactNode } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { EditButton, Show, useRecordContext } from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { RequireAccess } from '../auth/RequireAccess';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess, type AccessibleRegion } from '../auth/permissions';
import type {
  StoreDetail,
  StoreDetailChannel,
  StoreDetailMarket,
} from '../dataProvider';
import { formatRate } from './formatRate';

/**
 * Stores detail (Show) — the store as a single SCROLLING page, no tabs (D2-1).
 * Sections down the page: brand header, brand fields, then one section PER
 * MARKET the user is allowed to see (D2-2), each with that market's rate /
 * status / network and its read-only channels and tags. Channels, tags and
 * network are SHOWN read-only this pass (D2-4); only the brand fields and
 * per-market rate/active are editable, on the Edit page (D2-3).
 *
 * The record is the assembled store (`getOne('stores', merchantId)`), keyed by
 * the brand. The Edit affordance is gated `stores:write`, so a viewer never sees
 * it (and the edit route itself is guarded — see StoreEdit).
 */

const OWNERSHIP_CHIP: Record<
  string,
  { label: string; color: 'success' | 'warning' | 'default' }
> = {
  verified: { label: 'Ownership verified', color: 'success' },
  pending: { label: 'Ownership pending', color: 'warning' },
  unverified: { label: 'Ownership unverified', color: 'default' },
};

const OwnershipBadge = ({ status }: { status: string }) => {
  const chip = OWNERSHIP_CHIP[status] ?? {
    label: status,
    color: 'default' as const,
  };
  return (
    <Chip
      size="small"
      label={chip.label}
      color={chip.color}
      variant={chip.color === 'default' ? 'outlined' : 'filled'}
    />
  );
};

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary" component="div">
      {label}
    </Typography>
    <Typography variant="body2" component="div">
      {children}
    </Typography>
  </Box>
);

const ChannelsTable = ({ channels }: { channels: StoreDetailChannel[] }) => {
  if (channels.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No channels.
      </Typography>
    );
  }
  return (
    <Table size="small" aria-label="channels">
      <TableHead>
        <TableRow>
          <TableCell>Channel</TableCell>
          <TableCell>Rate</TableCell>
          <TableCell>Storefront / tracking URL</TableCell>
          <TableCell>Status</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {channels.map((c) => (
          <TableRow key={c.id}>
            <TableCell>{c.label}</TableCell>
            <TableCell>{formatRate(c.baseRate)}</TableCell>
            <TableCell sx={{ maxWidth: 320, wordBreak: 'break-all' }}>
              {c.trackingUrl ? (
                <Link href={c.trackingUrl} target="_blank" rel="noopener">
                  {c.trackingUrl}
                </Link>
              ) : (
                '—'
              )}
            </TableCell>
            <TableCell>{c.isActive ? 'Active' : 'Inactive'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const MarketSection = ({ market }: { market: StoreDetailMarket }) => (
  <Card variant="outlined" sx={{ mb: 2 }}>
    <CardContent>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mb: 1.5 }}
        useFlexGap
        flexWrap="wrap"
      >
        <Typography variant="h6" component="h3">
          {market.market.toUpperCase()}
        </Typography>
        <Chip
          size="small"
          label={market.active ? 'Active' : 'Inactive'}
          color={market.active ? 'success' : 'default'}
          variant={market.active ? 'filled' : 'outlined'}
        />
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 1,
          mb: 2,
        }}
      >
        <Field label="Cashback rate">{formatRate(market.baseRate)}</Field>
        <Field label="Affiliate network">{market.networkName ?? '—'}</Field>
      </Box>

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Channels
      </Typography>
      <ChannelsTable channels={market.channels} />

      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
        Tags
      </Typography>
      {market.tags.length > 0 ? (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {market.tags.map((t) => (
            <Chip key={t.key} size="small" label={t.label} variant="outlined" />
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No tags.
        </Typography>
      )}
    </CardContent>
  </Card>
);

const StoreShowLayout = () => {
  const record = useRecordContext<StoreDetail>();
  const { accessibleRegions } = useRegion();
  const { role } = useAccessContext();
  if (!record) return null;

  // D2-2: show every market section the user is allowed to see, stacked.
  const visibleMarkets = record.markets.filter((m) =>
    accessibleRegions.includes(m.market.toUpperCase() as AccessibleRegion),
  );

  const canEdit = hasAccess(
    { role, accessibleRegions },
    { capability: 'stores:write' },
  );

  return (
    <Box sx={{ maxWidth: 900 }}>
      {/* Brand header */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            useFlexGap
            flexWrap="wrap"
          >
            <Stack direction="row" spacing={2} alignItems="center" useFlexGap>
              <Box
                component="img"
                src={record.logoUrl ?? undefined}
                alt={record.name}
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 1,
                  objectFit: 'contain',
                  bgcolor: 'action.hover',
                }}
              />
              <Box>
                <Typography variant="h5" component="h1">
                  {record.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {record.category}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap>
              <OwnershipBadge status={record.ownershipVerificationStatus} />
              {canEdit && <EditButton label="Edit" />}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Brand fields */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" component="h2" sx={{ mb: 1.5 }}>
            Brand
          </Typography>
          <Field label="Description">{record.description}</Field>
          <Divider sx={{ my: 1.5 }} />
          <Field label="Founder(s)">
            {record.founderNames.length > 0
              ? record.founderNames.join(', ')
              : '—'}
          </Field>
          <Field label="Country of heritage">{record.countryOfHeritage}</Field>
          <Field label="Black-ownership evidence">
            {record.blackOwnershipEvidenceUrl ? (
              <Link
                href={record.blackOwnershipEvidenceUrl}
                target="_blank"
                rel="noopener"
                sx={{ wordBreak: 'break-all' }}
              >
                {record.blackOwnershipEvidenceUrl}
              </Link>
            ) : (
              '—'
            )}
          </Field>
        </CardContent>
      </Card>

      {/* Per-market sections */}
      <Typography variant="h6" component="h2" sx={{ mb: 1.5 }}>
        Markets
      </Typography>
      {visibleMarkets.length > 0 ? (
        visibleMarkets.map((m) => <MarketSection key={m.regionId} market={m} />)
      ) : (
        <Typography variant="body2" color="text.secondary">
          This store has no markets in your region scope.
        </Typography>
      )}
    </Box>
  );
};

/** The detail view. Exported for testing without the access wrapper. */
export const StoreShowView = () => (
  <Show title="Store" actions={false} resource="stores">
    <StoreShowLayout />
  </Show>
);

/** Gated entry point registered as the `stores` Resource's show. */
export const StoreShow = () => (
  <RequireAccess capability="stores:read">
    <StoreShowView />
  </RequireAccess>
);
